import { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { Row, Col } from 'antd';

const styles = {
  chipContainer: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#fafafa',
    flexWrap: 'wrap',
  },
  chip: {
    fontSize: '14px',
    height: '36px',
  },
};

const RuleType = ({ formData, setFormData, entities, attributes }) => {
  const selectedAttributes = formData?.applies_to || [];

  const availableAttributes = attributes?.length
    ? attributes.map((attribute) => attribute.name)
    : ['Phone', 'MobilePhone'];

  //  Initialize rule_type as "built-in" by default
  useEffect(() => {
    if (!formData.rule_type) {
      setFormData((prev) => ({
        ...prev,
        rule_type: 'built-in',
      }));
    }
  }, [formData.rule_type, setFormData]);

  // ✅ Handle rule type change
  const handleProviderTypeChange = (e, newValue) => {
    if (newValue !== null) {
      setFormData((prev) => ({
        ...prev,
        rule_type: newValue,
      }));
    }
  };

  // ✅ Handle "Applies To" chip toggle
  const handleAttributeClick = (attr) => {
    const newAttributes = selectedAttributes.includes(attr)
      ? selectedAttributes.filter((a) => a !== attr)
      : [...selectedAttributes, attr];

    setFormData((prev) => ({
      ...prev,
      applies_to: newAttributes,
      configuration: {
        ...prev.configuration,
        applies_to: newAttributes, // ✅ stays only inside configuration
      },
    }));
  };

  // ✅ Handle text input changes
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const showNameAndDescription = formData.rule_type !== 'built-in';

  return (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        borderRadius: 3,
        backgroundColor: '#fafafa',
      }}
    >
      <Row gutter={[32, 24]} align="top">
        {/* Left Section */}
        <Col xs={24} md={12}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
            Rule Configuration
          </Typography>

          <Box display="flex" flexDirection="column" gap={3}>
            {/* Rule Type Buttons — moved to top */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#555' }}>
                Rule Type
              </Typography>
              <ToggleButtonGroup
                color="primary"
                value={formData.rule_type || 'built-in'}
                exclusive
                onChange={handleProviderTypeChange}
                fullWidth
              >
                <ToggleButton value="built-in" sx={{ textTransform: 'none', py: 1 }}>
                  Built-in
                </ToggleButton>
                <ToggleButton value="external-api-predefined" sx={{ textTransform: 'none', py: 1 }}>
                  External API (Predefined)
                </ToggleButton>
                <ToggleButton value="external-api-generic" sx={{ textTransform: 'none', py: 1 }}>
                  External API (Generic)
                </ToggleButton>
                <ToggleButton value="custom-script" sx={{ textTransform: 'none', py: 1 }}>
                  Custom Script
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Applies To Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#555' }}>
                Applies to (attributes)
              </Typography>
              <div style={styles.chipContainer}>
                {availableAttributes.map((attr) => (
                  <Chip
                    key={attr}
                    label={attr}
                    onClick={() => handleAttributeClick(attr)}
                    color={selectedAttributes.includes(attr) ? 'primary' : 'default'}
                    variant={selectedAttributes.includes(attr) ? 'filled' : 'outlined'}
                    style={styles.chip}
                  />
                ))}
              </div>
            </Box>

            {/* Rule Name and Description — hidden for built-in */}
            {showNameAndDescription && (
              <>
                <TextField
                  label="Rule Name"
                  variant="outlined"
                  fullWidth
                  value={formData.rule_name || ''}
                  onChange={handleChange('rule_name')}
                  InputProps={{
                    sx: { borderRadius: 2, backgroundColor: 'white' },
                  }}
                />

                <TextField
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description || ''}
                  onChange={handleChange('description')}
                  InputProps={{
                    sx: { borderRadius: 2, backgroundColor: 'white' },
                  }}
                />
              </>
            )}
          </Box>
        </Col>

        {/* Right Section */}
        <Col xs={24} md={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f5f8ff',
              border: '1px solid #dbe3ff',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: '#2a3eb1' }}>
              What’s a Cleanse Rule?
            </Typography>
            <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.7 }}>
              Cleanse rules transform inbound values before matching and survivorship. You can chain multiple rules —{' '}
              <strong>order matters</strong>. Common examples include trimming/normalizing text, formatting phone
              numbers, fixing email syntax, verifying addresses, filtering profanity, and enriching data.
            </Typography>
          </Paper>
        </Col>
      </Row>
    </Paper>
  );
};

export default RuleType;
